// src/shared/hooks/useTableSort.ts
//
// Lightweight client-side table sorting for small, already-in-memory Mantine
// tables (legacy reports, sub-accounts). Composes AFTER any search/filter step:
// pass the already-filtered rows in. Pair the returned sortKey/sortDir/onSort
// with SortableTh header cells. For virtualized/server-sorted tables use the
// feature-specific engine instead.

import { useCallback, useMemo, useState } from 'react';
import { TableSort, type SortAccessor, type SortDirection } from '@/shared/utils/tableSort';

export type { SortDirection };

export interface TableSortColumn<T> {
  accessor: SortAccessor<T>;
  /** Direction applied when first switching TO this column (default 'asc'). */
  defaultDir?: SortDirection;
}

export interface UseTableSortResult<T, K extends string> {
  sorted: T[];
  sortKey: K;
  sortDir: SortDirection;
  onSort: (key: K) => void;
}

/**
 * Sort `rows` by one of the declared `columns`. Clicking the active column flips
 * direction; clicking a new column switches to it at its `defaultDir` (or 'asc').
 * Pass a STABLE `columns` object (module constant or useMemo) to avoid needless
 * re-sorts.
 */
export function useTableSort<T, K extends string>(
  rows: T[],
  columns: Record<K, TableSortColumn<T>>,
  // NoInfer keeps `initial.key` from narrowing K — K must come from `columns`,
  // otherwise the seed key (e.g. 'views') would collapse the whole key union.
  initial: { key: NoInfer<K>; dir: SortDirection },
): UseTableSortResult<T, K> {
  const [sortKey, setSortKey] = useState<K>(initial.key);
  const [sortDir, setSortDir] = useState<SortDirection>(initial.dir);

  const onSort = useCallback(
    (key: K) => {
      if (key === sortKey) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir(columns[key].defaultDir ?? 'asc');
      }
    },
    [sortKey, columns],
  );

  const sorted = useMemo(
    () => TableSort.sortRows(rows, columns[sortKey].accessor, sortDir),
    [rows, columns, sortKey, sortDir],
  );

  return { sorted, sortKey, sortDir, onSort };
}
